import { BrowserHistory, createBrowserHistory } from 'history';
import React, { ReactElement, Suspense, useState } from 'react';

import {
    BrowserRouterProps,
    NavigationType,
    Router,
    Location,
} from 'react-router-dom';

export interface FutureConfig {
    v7_normalizeFormMethod: boolean;
    v7_prependBasename: boolean;
    v7_startTransition: boolean;
}

const SuspenseTrigger = (): ReactElement => {
    throw new Promise(() => {});
};

interface BrowserRouterWithHistory extends BrowserRouterProps {
    history?: BrowserHistory;
    future?: FutureConfig;
}
const START_TRANSITION = 'startTransition';
const startTransitionImpl = React[START_TRANSITION];

interface BrowserMetaProps {
    backState: any;
    setBackState: React.Dispatch<React.SetStateAction<any>>;
    isDocumentReload: boolean;
    setDocumentReload: React.Dispatch<React.SetStateAction<boolean>>;
}

export const BrowserMeta = React.createContext<BrowserMetaProps | null>(null);
/**
 * A `<Router>` for use in web browsers. Provides the cleanest URLs.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function BrowserRouter({
    basename,
    children,
    future,
    window,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    history: ownHistory,
}: BrowserRouterWithHistory) {
    const historyRef = React.useRef<BrowserHistory>();
    const historyAPIRef = React.useRef<BrowserHistory>();
    if (historyRef.current == null) {
        if (ownHistory) {
            historyRef.current = ownHistory;
        } else {
            historyRef.current = createBrowserHistory({
                window,
                // v5Compat: true,
            });
        }
    }
    if (historyAPIRef.current == null) {
        historyAPIRef.current = createBrowserHistory({
            window,
            // v5Compat: true,
        });
    }

    const history = historyRef.current;
    const historyAPI = historyAPIRef.current;
    const [backState, setBackState] = useState<Location | null>(null);
    const [isDocumentReload, setDocumentReload] = useState<boolean>(false);
    const [isRelease, setRelease] = useState<boolean>(false);
    const [state, setStateImpl] = React.useState({
        action: history.action,
        location: historyAPI.location,
    });
    // eslint-disable-next-line camelcase
    const { v7_startTransition } = future || {};
    const setState = React.useCallback(
        (newState: { action: NavigationType; location: Location }) => {
            if (
                newState &&
                newState.location &&
                newState.location.pathname !== '/single-reviewed' &&
                newState.location.pathname !== '/reviewer-viewer'
            ) {
                setBackState(null);
            }

            setDocumentReload(false);
            // eslint-disable-next-line camelcase, no-unused-expressions
            v7_startTransition && startTransitionImpl
                ? startTransitionImpl(() => setStateImpl(newState))
                : setStateImpl(newState);
        },
        [setStateImpl, v7_startTransition]
    );

    React.useLayoutEffect(() => {
        history.listen(setState);
        if (isRelease === false) {
            setRelease(true);
            setStateImpl((historic) => ({
                action: history.action,
                location:
                    history.location && historic.location
                        ? {
                              ...historic.location,
                              state:
                                  historic.location && historic.location.state
                                      ? historic.location.state
                                      : history.location.state,
                          }
                        : historic.location,
            }));
        }
    }, [history, setState]);

    const initialBrowserMeta = React.useMemo(
        () => ({
            setBackState,
            backState,
            isDocumentReload,
            setDocumentReload,
        }),
        [backState, isDocumentReload]
    );
    return (
        <BrowserMeta.Provider value={initialBrowserMeta}>
            <Suspense>
                {isRelease ? (
                    <Router
                        basename={basename}
                        // eslint-disable-next-line react/no-children-prop
                        children={children}
                        location={state.location}
                        navigationType={state.action}
                        navigator={history}
                    />
                ) : (
                    <SuspenseTrigger />
                )}
            </Suspense>
        </BrowserMeta.Provider>
    );
}
